/// <reference path="../../../@types/yieldlove.d.ts" />
import type { YieldloveModuleSettings } from '@mbrtargeting/metatag-config-types';
import { SystemEvent, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types';
import type { MetaTagCoreInterface, SystemSlotEventDetail } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { debounce, isNotUndefined, isObject, isStringArray, partition, simpleStorageSwitch, timelimitPromise, wait } from '@mbrtargeting/metatag-utils';
import { AdslotRegistry } from 'src/loader/modules/adslot-registry.js';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onSystemEvent } from '../../../loader/decorators/on-system-event.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectModuleSettings, selectPositionSettings } from '../../../loader/essentials/config-selector.js';
import { waitForEventPromise } from '../../../loader/essentials/events.js';
import { logger } from '../../../loader/essentials/logger.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { ADSLOT_REGISTRY, CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { markSdg, measurePerformance } from '../../../utils/performance.js';
import { GLOBAL_SETTINGS } from '../../token.js';
import { setIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { Adslot, isAdslotInstance } from '../adslot-controller/adslot.js';
import { CreativeSource, CreativeSourceLoadSlotsParams } from '../adslot-controller/creative-source.js';
import { GlobalSettings } from '../global-settings/global-settings.js';
import { BeaconType, triggerBeacon } from './beacon.js';

const log = logger.logGroup({ prefix: 'Yieldlove' });

declare const window: Window & yieldlove.Window & {
    SDG: MetaTagCoreInterface
};

const win = window;

const ylStartAuction = (adslotNames: string[]) => new Promise<yieldlove.TargetingsPerId>(resolve => {
    // TODO would like to use startAuction with callback, but yl handles direct rendering in the default-callback
    // window.YLHH?.bidder?.startAuction(adslotNames, resolve);

    const perfMeasure = adslotNames.map(name => measurePerformance(`Auction-${name.toUpperCase()}-YL`).start());
    // as workaround wait for SDG_YIELDLOVE_AUCTION_DONE event once sent by yl
    const listener = () => {
        win.removeEventListener(SystemEvent.SDG_YIELDLOVE_AUCTION_DONE, listener);
        markSdg(`auction-phase-ended_mt`);
        perfMeasure.forEach(entry => entry.end());
        resolve({});
    };
    win.addEventListener(SystemEvent.SDG_YIELDLOVE_AUCTION_DONE, listener);
    markSdg(`auction-phase-started_mt`);
    window.YLHH?.bidder?.startAuction(adslotNames);
});

export const timelimitPromise2 = <T>(promise: Promise<T>, ms: number = -1): Promise<{ isTimeout: boolean; result?: T }> => Promise.race([
    wait(ms).then(() => ({ isTimeout: true })),
    promise.then(result => ({ isTimeout: false, result })),
]);

const ylAdUnitByCode = (code: string): yieldlove.AdUnit | undefined => (window.YLHH?.bidder?.adUnits || []).find(adUnit => adUnit.codes.includes(code));

@injectionTarget()
export class Yieldlove implements CreativeSource {

    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(GLOBAL_SETTINGS) private globalSettings!: GlobalSettings;
    @inject(CONSENT_CACHE) private consentCache!: ConsentCache;
    @inject(ADSLOT_REGISTRY) private adslotRegistry!: AdslotRegistry;

    private readonly settings: YieldloveModuleSettings | undefined;
    private readonly bidderReady: Promise<boolean>;

    constructor() {
        this.settings = this.configResolver.get(selectModuleSettings('PREBID'));
        this.bidderReady = this.instantiateBidder().catch(() => false);
    }

    /**
     * Instantiates bidder to inform PreBid which AdSlot layout is present on the site. Return a promise to hook upon.
     *
     * If we find "layouts" in our PREBID Module config, wait for the Publisher to either set a zone or additionally a pageType. Based on this information, we select the correct layout.
     * No "layouts" will instantiate immediately after YL script is loaded.
     */
    async instantiateBidder(): Promise<boolean> {
        const { active = false, yieldloveDomainName = '', layouts = [], staticAdLayout = '' } = this.settings || {};
        if (!active) return false;

        // create cmd queue for bidder and google, bidder seems dependent on google queue and wont work without
        win.googletag = win.googletag || {};
        win.googletag.cmd = win.googletag.cmd || [];
        win.pbjsYLHH = win.pbjsYLHH || {};
        win.pbjsYLHH.que = win.pbjsYLHH.que || [];

        await Promise.all([
            // wait for TARGETING_PHASE to complete
            waitForEventPromise(SystemEvent.DELIVERY_PHASE_STARTED),
            // wait for yieldlove to be ready
            new Promise<void>(resolve => win.pbjsYLHH.que.push(resolve)),
        ]);

        win.YLHH.bidder = new win.YLHH.Bidder(yieldloveDomainName, [], { mode: 'online' }, win.googletag, win.pbjsYLHH, win.SDG);
        log.debug('bidder instantiated');

        // select layout based either on url parameter, configured "staticAdLayout", or layout map consisting of zones and pageTypes
        const selectAdLayout = () => {
            // if staticAdLayout is configured or passed via url param, use this first
            if (staticAdLayout) return staticAdLayout;
            // getting current zone and page once to use them multiple times when filtering layouts
            const zone = this.globalSettings.getZone();
            const page = this.globalSettings.getPageType();
            // find matching adLayout name from config. if zone or page is not set, treat as wildcard (matches all)
            const { name = '' } = layouts.find(({ zones, pages }) =>
                (!zones?.length || zones.includes(zone)) &&
                (!pages?.length || pages.includes(page))
            ) || {};
            return name;
        };
        // debounce updateAdLayout to avoid multiple calls in short time
        const updateAdLayout = debounce(() => {
            // get adLayout, if one is found, let yieldlove know.
            const layout = selectAdLayout();
            if (layout) {
                log.info('using adlayout "%s"', [layout]);
                try {
                    win.YLHH.bidder.setAdLayout(`${yieldloveDomainName}_${layout}`);
                } catch (e) {
                    log.error('failed to set adlayout "%s", error %o', [layout, e]);
                }
            }
        }, 10);

        // update adLayout on zone or pageType change
        win.addEventListener(SystemEvent.SDG_ZONE_SET, updateAdLayout);
        win.addEventListener(SystemEvent.SDG_PAGETYPE_SET, updateAdLayout);
        // update adLayout initially
        updateAdLayout();

        return true;
    }

    @onSystemEvent(SystemEvent.SDG_CORE_FILE_LOADED)
    async setupEventListeners() {
        // listen for yieldlove reload events
        win.addEventListener(SystemSlotEvent.SDG_SLOT_WILL_RELOAD, ({ detail }) => {

            const adslot = this.adslotRegistry.getAdslot(detail.slotName);
            if (!isAdslotInstance(adslot)) return;

            if (simpleStorageSwitch('sdgSuppressReload', /^(1)$/, true)) {
                log.debug('%s: adslot reload suppressed', [adslot.slotName.toUpperCase()]);
                return;
            }

            const { reloadOnVisibility = false } = this.settings || {};
            const { canReloadOnVisibility = false } = this.configResolver.get(selectPositionSettings(adslot.slotName)) || {};
            const executeSlotReload = ({ detail }: CustomEvent<SystemSlotEventDetail>) => {
                if (adslot.slotName === detail.slotName) {
                    win.removeEventListener(SystemSlotEvent.SDG_SLOT_DIRECT_RENDERING, executeSlotReload);
                    win.removeEventListener(SystemSlotEvent.SDG_SLOT_AD_SERVER_RENDERING, executeSlotReload);
                    adslot.markLoadIntention({
                        forceReload: true,
                        runAuction: false, // auction was already done by yieldlove
                    });
                }
            };

            const doReloadOnVisibility: boolean = reloadOnVisibility && canReloadOnVisibility;
            log.debug('%s: adslot will be reloaded: %s', [adslot.slotName.toUpperCase(), doReloadOnVisibility]);
            if (doReloadOnVisibility) {
                win.addEventListener(SystemSlotEvent.SDG_SLOT_DIRECT_RENDERING, executeSlotReload);
                win.addEventListener(SystemSlotEvent.SDG_SLOT_AD_SERVER_RENDERING, executeSlotReload);
                adslot.setTargeting({ ri: ['1'] });
                // experiment(TICKETEXT-4261): remove old targetings as yieldlove does not clear orphaned keys from past auctions
                for (const [key] of adslot.getTargeting().filter(([key]) => /^(yieldlove|hb)\_/.test(key))) {
                    adslot.removeTargeting(key);
                }
            }
        });

        // auction process - for each slot that throws error in auction process
        win.addEventListener(SystemSlotEvent.SDG_SLOT_AUCTION_ERROR, ({ detail }) => {
            log.error('%s: received SDG_SLOT_AUCTION_ERROR', [detail.slotName?.toUpperCase()]);
        });

        // auction process - for each slot that finishes auction process
        win.addEventListener(SystemSlotEvent.SDG_SLOT_AUCTION_COMPLETE, ({ detail }) => {
            log.info('%s: received SDG_SLOT_AUCTION_COMPLETE', [detail.slotName?.toUpperCase()]);
        });

        // after auction complete - bidder decides adserver or direct rendering
        win.addEventListener(SystemSlotEvent.SDG_SLOT_DIRECT_RENDERING, ({ detail }) => {
            log.info('%s: received SDG_SLOT_DIRECT_RENDERING', [detail.slotName?.toUpperCase()]);
            markSdg(`direct-rendering-decided_mt_${detail.slotName}`);
        });

        // after auction complete - bidder decides adserver or direct rendering
        win.addEventListener(SystemSlotEvent.SDG_SLOT_AD_SERVER_RENDERING, ({ detail }) => {
            log.info('%s: received SDG_SLOT_AD_SERVER_RENDERING', [detail.slotName?.toUpperCase()]);
        });

        // if all slots have finished their auction process
        win.addEventListener(SystemEvent.SDG_YIELDLOVE_AUCTION_DONE, () => {
            log.info('received SDG_YIELDLOVE_AUCTION_DONE');
        });

        // an adslot finishes rendering via direct rendering
        win.addEventListener(SystemSlotEvent.SDG_SLOT_DIRECT_RENDERING_COMPLETE, ({ detail }) => {
            log.info('%s: received SDG_SLOT_DIRECT_RENDERING_COMPLETE', [detail.slotName?.toUpperCase()]);
        });

        // an adslot can not render with direct rendering
        win.addEventListener(SystemSlotEvent.SDG_SLOT_YL_RENDER_ERROR, ({ detail }) => {
            log.info('%s: received SDG_SLOT_YL_RENDER_ERROR', [detail.slotName?.toUpperCase()]);
        });

        win.addEventListener('yieldlove.auctionEnded', (event) => {
            log.info('received yieldlove.auctionEnded', [(event as CustomEvent<unknown>).detail]);
        });

        win.pbjsYLHH = win.pbjsYLHH || {};
        win.pbjsYLHH.que = win.pbjsYLHH.que || [];
        win.pbjsYLHH.que.push(() => {
            win.pbjsYLHH.onEvent('adRenderSucceeded', ({ bid }) => {
                const ylAdUnit = ylAdUnitByCode(bid?.adUnitCode);
                if (ylAdUnit && isStringArray(ylAdUnit.codes)) {
                    for (const slotName of ylAdUnit.codes) {
                        const adSlot = win.SDG.Advertising.getSlotByName(slotName);

                        //YL wrapper does have multiple code entries per slot. Only use the one that lead to our adSlots.
                        if (!adSlot) continue;

                        // When a blocker is delivered, the Anzeige flag is displayed in a short period.
                        if (bid.bidder === 'stroeerCore' && bid.tracking?.campaignType === 'SPONSORSHIP' && bid.width === 1 && bid.height === 1) {
                            adSlot.reportBlockerDelivery(true);
                        } else {
                            adSlot.reportAdDelivery(bid.width, bid.height, false);
                        }

                        setIdsForAdSlot(slotName, {
                            source: 'yieldlove',
                            values: {
                                lastSsp: bid.bidderCode,
                                realSize: `${bid.width}x${bid.height}`,
                                preBidAuctionId: bid.auctionId,
                                preBidAdId: bid.adId,
                                auctionId: bid.auctionId,
                                requestId: bid.requestId,
                                size: `${bid.width}x${bid.height}`,
                                isEmpty: false, // always filled when adRenderSucceeded
                                creativeId: bid.creativeId,
                                // campaignId, buyerId, buyerName, creativeId are set by MT1 in infotool.js on metaTagSystemSlotDone event
                                ...(isObject(bid.tracking) && {
                                    campaignId: `${bid.tracking.campaignId}`,
                                    buyerName: `${bid.tracking.buyerName}`,
                                    creativeId: `${bid.tracking.creativeId}`,
                                }),
                                ...(bid.bidderCode?.startsWith('stroeer') && {
                                    creativeLinkId: bid.ad?.match(/https:\/\/(trx\.adscale\.de|tracking\.m6r\.eu)\/.*creativeLinkId(\=|\\u003d|%3d)(.*?)(\&|\\u0026|%26)/i)?.[3],
                                    coreCampaignId: bid.meta?.coreCampaignId ?? bid.ad?.match(/https:\/\/(trx\.adscale\.de|tracking\.m6r\.eu)\/.*cci(\=|\\u003d|%3d)(.*?)(\&|\\u0026|%26)/i)?.[3],
                                    coreCreativeId: bid.meta?.coreCreativeId,
                                }),
                            },
                        });
                    }
                }
            });
        });
    }

    public async loadSlots({ adslots, done, context }: CreativeSourceLoadSlotsParams) {

        const { active = false, iabVendorId = 251, yieldloveDomainName = '', waitForAuctionTime = 3000, enableBeacons = false, skipAuctionConsentCheck = false } = this.settings || {};

        // if yieldlove is disabled, send all adslots to the next creative source
        if (!active) {
            log.debug('not active, skipping slots %o', [adslots.map(s => s.slotName.toUpperCase())]);
            done(adslots);
            return;
        }

        const isAllowed = skipAuctionConsentCheck || this.consentCache.ifConsentConditions([
            { consentedVendors: [iabVendorId] },
            { legitimateInterestVendors: [iabVendorId] },
            { gdprApplies: false },
        ]);

        //if no consent for yieldlove IAB vendor, send slots to next creative source
        if (!isAllowed) {
            log.warn('no consent, skipping slots %o', [adslots.map(s => s.slotName.toUpperCase())]);
            done(adslots);
            return;
        }

        log.debug('calling slots: %o', [adslots.map(s => s.slotName.toUpperCase())]);

        // wait for yieldlove script to load completely, it executes its queue when ready to process
        // if it runs into timeout, send all adslots to next creative source
        const isReady = await timelimitPromise(this.bidderReady, 5000);

        if (!isReady) {
            log.warn('5 second timeout for PreBid reached. YL Script not loaded or bidder not instantiated. Slots will be passed to next creative source: %o', [adslots.map(s => s.slotName.toUpperCase())]);

            //flag = cbl on adslot will deliver a blocker campaign, so GAM might deliver a special campaign on all affected slots for sponsorship campaigns.
            adslots.forEach(adslot => adslot.setTargeting({ flag: 'cbl' }));

            if (enableBeacons) triggerBeacon({ type: BeaconType.LOAD_TIMEOUT, source: yieldloveDomainName });
            done(adslots);
            return;
        }

        // helper function to invoke all existing directRendingFunctions of the adslots
        const invokeDirectRendingFunctions = (adslots: Adslot[]) => Promise.allSettled(
            adslots.map(adslot => adslot.directRenderingFunction).filter(isNotUndefined).map(fn => fn())
        );

        // map metadata rtaAuction and optional ylAdUnit to each adslot
        const list = adslots.map(adslot => ({
            adslot,
            runAuction: context(adslot).runAuction,
            rtaAuction: this.configResolver.get(selectPositionSettings(adslot.slotName))?.rtaAuction || false,
            ylAdUnit: ylAdUnitByCode(adslot.slotName),
        }));

        // split the list into two partitions
        //   - only items with rtaAuction enabled and an existing yieldlove adunit can be sent into auction
        //   - skip auction on reload by yieldlove (auction already done in yieldlove impl)
        const [rtaEnabledItems, rtaDisabledItems] = partition(list, e => e.rtaAuction && e.runAuction && !!e.ylAdUnit);

        // for rta disabled adslots, send immediately to next creative source
        if (rtaDisabledItems.length) {
            log.info('skipping auction (!rtaAuction=%o, !ylAdUnit=%o, !runAuction=%o)', [
                rtaDisabledItems.filter(e => !e.rtaAuction).map(e => e.adslot.slotName),
                rtaDisabledItems.filter(e => !e.ylAdUnit).map(e => e.adslot.slotName),
                rtaDisabledItems.filter(e => !e.runAuction).map(e => e.adslot.slotName),
            ]);
            const rtaDisabledSlots = rtaDisabledItems.map(e => e.adslot);
            await invokeDirectRendingFunctions(rtaDisabledSlots); // reloaded slots are already auctioned and can eventually directly rendered
            done(rtaDisabledSlots);
        }

        // if there are not rta enabled slots, we can not run auction and do not need to pass anything to next creative source
        if (!rtaEnabledItems.length) {
            log.debug('can not run auction (empty slot list)');
            return;
        }

        // run yieldlove auction and process retured targeting
        const rtaEnabledSlots = rtaEnabledItems.map(e => e.adslot);
        const rtaEnabledSlotNames = rtaEnabledSlots.map(s => s.slotName);
        log.info('sending to auction %o', [rtaEnabledSlotNames]);
        const { isTimeout, result: targetingsPerId = {} } = await timelimitPromise2(ylStartAuction(rtaEnabledSlotNames), waitForAuctionTime) || {};
        if (isTimeout) {
            log.warn('auction wait timeout (%sms)', [waitForAuctionTime, rtaEnabledSlotNames]);

            //flag = cbl on adslot will deliver a blocker campaign, so GAM might deliver a special campaign on all affected slots for sponsorship campaigns.
            //all slots of the auction should receive the cbl value. GAM will then decide on which slots to act.
            rtaEnabledSlots.forEach(adslot => adslot.setTargeting({ flag: 'cbl' }));

            if (enableBeacons) triggerBeacon({ type: BeaconType.AUCTION_TIMEOUT, source: yieldloveDomainName });
        } else {
            log.info('auction done', [rtaEnabledSlotNames, targetingsPerId]);
            if (enableBeacons) triggerBeacon({ type: BeaconType.AUCTION_COMPLETE, source: yieldloveDomainName });
        }
        Object.entries(targetingsPerId).forEach(([ylId, targeting]) => {
            const { adslot } = rtaEnabledItems.find(e => e.ylAdUnit?.id === +ylId) || {};
            adslot?.setTargeting(targeting);
        });

        await invokeDirectRendingFunctions(rtaEnabledSlots);
        done(rtaEnabledSlots);
    }
}
